/**
 * "My challenges changed": one signal for every list of the player's own challenges.
 *
 * History, home's and the profile's Recent challenges, the profile's stat tiles, Create's count line and its "N open"
 * sheet, and the Take queue are snapshots of a read made when the screen opened. Challenges move without the player
 * doing anything (a taker arrives; a taker walks away and the abandoned-seat sweep forfeits the seat; the other seat
 * finishes while this app sits in the background), so a list left open or backgrounded across one kept saying
 * "OPPONENT PLAYING". This is the cue to read it again. (Blockari's `useOnMyChallengesChanged`, 507fcff.)
 *
 * It fires on (a) the app coming back to the foreground (visibilitychange to visible, focus, pageshow, back online) and
 * (b) a notice that one of the player's challenges moved: the closed set below, from the stream or from a foreground
 * re-read that finds one it had not heard (read or not). 21 Wild has no direct-challenge kind (Blockari's
 * `challenge_direct_expired`): a "Challenge Me" card is a profile link, never a challenge, so there is nothing to add.
 *
 * Debounced (trailing) so a foreground and the notice that was waiting there fire ONE read, not two. Pure on purpose,
 * with the timers and the event targets injected: the wiring in `main.ts` is a few lines and everything that can go
 * wrong is here under test.
 */

/**
 * The notice kinds that say one of my challenges changed: taken (waiting to being played), and every way one ends
 * (won, lost, expired, voided; a forfeit by the sweep writes the same won/lost rows). A chest changes no list.
 */
export const CHANGE_KINDS: readonly string[] = [
  'challenge_taken',
  'challenge_won',
  'challenge_lost',
  'challenge_expired',
  'challenge_voided',
];

export function changesMyChallenges(kind: string): boolean {
  return CHANGE_KINDS.includes(kind);
}

/** Foreground plus a notice land within a beat of each other; this folds them into one fetch. */
export const CHANGED_DEBOUNCE_MS = 500;

/** Why it fired: a notice always re-reads; a foreground may be skipped by a list that was just read (`LoadClock`). */
export type ChangeCause = 'notice' | 'foreground';

export interface ChangedSignal {
  /**
   * Something may have changed: run the subscribers once, after the debounce, however many times this was called.
   * A burst that includes a notice is a `'notice'` run.
   */
  fire(cause?: ChangeCause): void;
  /** Subscribe; returns the way out. */
  subscribe(listener: (cause: ChangeCause) => void): () => void;
  /** Drop a pending fire (sign-out). */
  cancel(): void;
}

export interface SignalOptions {
  readonly delayMs?: number;
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
}

export function createChangedSignal(options: SignalOptions = {}): ChangedSignal {
  const delayMs = options.delayMs ?? CHANGED_DEBOUNCE_MS;
  const setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const listeners = new Set<(cause: ChangeCause) => void>();
  let pending: unknown = null;
  let cause: ChangeCause = 'foreground';

  const cancel = (): void => {
    if (pending !== null) clearTimer(pending);
    pending = null;
    cause = 'foreground';
  };

  return {
    fire(why: ChangeCause = 'notice'): void {
      // Trailing: each call restarts the wait, so a burst is one run, after the last of it. A notice in the burst wins.
      if (why === 'notice') cause = 'notice';
      if (pending !== null) clearTimer(pending);
      pending = setTimer(() => {
        pending = null;
        const ran = cause;
        cause = 'foreground';
        for (const listener of [...listeners]) listener(ran);
      }, delayMs);
    },
    subscribe(listener: (cause: ChangeCause) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    cancel,
  };
}

/** Focus and visibilitychange arrive together; a list read this recently is not read again for a foreground. */
export const FOREGROUND_GAP_MS = 4000;

/**
 * When each list was last loaded (by its screen opening or by a refresh), so a foreground within
 * `FOREGROUND_GAP_MS` of that load is skipped for that list. A notice is never skipped: it says something changed.
 */
export interface LoadClock {
  stamp(job: RefreshJob): void;
  due(job: RefreshJob, cause: ChangeCause): boolean;
}

export function createLoadClock(now: () => number = Date.now): LoadClock {
  const last = new Map<RefreshJob, number>();
  return {
    stamp: (job) => void last.set(job, now()),
    due(job, cause) {
      if (cause === 'notice') return true;
      const at = last.get(job);
      return at === undefined || now() - at >= FOREGROUND_GAP_MS;
    },
  };
}

export const loadClock: LoadClock = createLoadClock();

/**
 * Ordering for a list that is fetched more than once: `begin()` before the request, and the answer is applied only if
 * the returned check still says it is the newest request started. A slow older response that lands after a newer one
 * is dropped rather than put back over it.
 */
export interface LatestGate {
  begin(): () => boolean;
}

export function createLatestGate(): LatestGate {
  let latest = 0;
  return {
    begin(): () => boolean {
      const mine = ++latest;
      return () => mine === latest;
    },
  };
}

/** The minimal shapes of `window` and `document` the foreground listener needs, so it can be tested without a DOM. */
export interface ForegroundTarget {
  addEventListener(type: string, listener: () => void): void;
}
export interface ForegroundDocument extends ForegroundTarget {
  readonly visibilityState: string;
}

/**
 * Fire the signal when the app comes back to the foreground: the tab becoming visible (`visibilitychange`), the window
 * taking focus, a page restored from the back/forward cache (`pageshow`), or the network returning (`online`; a phone
 * that slept has also lost its stream). Hidden is not a return, so it fires nothing.
 */
export function watchForeground(signal: ChangedSignal, win: ForegroundTarget, doc: ForegroundDocument): void {
  const back = (): void => {
    if (doc.visibilityState === 'visible') signal.fire('foreground');
  };
  // On the document, where the browser fires it, and on the window, where the old queue listener had it: a second
  // call for the same return folds into the first in the debounce.
  doc.addEventListener('visibilitychange', back);
  win.addEventListener('visibilitychange', back);
  for (const type of ['focus', 'pageshow', 'online']) win.addEventListener(type, back);
}

/** One re-read of the player's own challenges, for one surface that shows them. */
export type RefreshJob =
  | 'queue'
  | 'my-counts'
  | 'history'
  | 'profile-recent'
  | 'profile-stats'
  | 'home-recent'
  | 'own-wagers';

export interface RefreshContext {
  /** Whether the app is in the foreground. Nothing is read for a page nobody is looking at. */
  readonly visible: boolean;
  readonly signedIn: boolean;
  /** The screen up: `ui.screen`. */
  readonly screen: string;
  /** The profile up is the viewer's own. */
  readonly ownProfile: boolean;
  /** Create's "N open" sheet is open. */
  readonly ownWagersOpen: boolean;
}

/**
 * What to read again when the signal fires: exactly the lists on screen right now, and nothing at all for a hidden
 * app, a signed-out one, or a screen that shows none of them. (Home's Recent card, History, the own profile's Recent
 * and stat tiles, Create's count line and its "N open" sheet, the Take queue.)
 */
export function jobsFor(ctx: RefreshContext): RefreshJob[] {
  if (!ctx.visible || !ctx.signedIn) return [];
  switch (ctx.screen) {
    case 'take':
      return ['queue', 'my-counts'];
    case 'history':
      return ['history'];
    case 'profile':
      return ctx.ownProfile ? ['profile-recent', 'profile-stats'] : [];
    case 'start':
      return ['home-recent'];
    case 'create':
      return ctx.ownWagersOpen ? ['my-counts', 'own-wagers'] : ['my-counts'];
    default:
      return [];
  }
}
